// src/store/SettingsContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import i18n from "../i18n";
import {
  LANG_KEY,
  isManualLanguageChoice,
} from "../utils/languagePref";

const SettingsContext = createContext(null);

const RTL_LANGS = ["ar", "fa", "he"];

export function SettingsProvider({ children }) {
  const [language, setLanguageState] = useState(() => i18n.language || "en");

  const [theme, setTheme] = useState("light");
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  useEffect(() => {
    const lang = language || "en";

    // ✅ persist only after explicit user choice (LanguagePage)
    if (isManualLanguageChoice()) {
      window.localStorage.setItem(LANG_KEY, lang);
    }

    // ✅ apply i18n
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }

    // ✅ apply dir/lang
    const dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
  }, [language]);

  const setLanguage = (lang) => {
    if (!lang) return;
    setLanguageState(lang);
  };

  const toggleTheme = () =>
    setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      theme,
      toggleTheme,
      notificationsEnabled,
      setNotificationsEnabled,
    }),
    [language, theme, notificationsEnabled]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
}
